/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useServerlessFeatureFlag } from '@modules/launchdarkly'
import usePermissions from '@modules/permissions-lib/hooks'
import { useListSecurityProjectsQuery } from '@modules/project-user-lib/hooks/list'

import type { UseQueryOptions } from 'react-query'
import type { ResourceQueryResult } from '../types'

export const useResourcesQuery = (
  options?: Pick<UseQueryOptions, 'enabled'>,
): ResourceQueryResult => {
  const enabled = options?.enabled ?? true

  const isServerlessEnabled = useServerlessFeatureFlag()

  const hasListSecurityPermission = usePermissions(
    [{ type: 'project-security', action: 'list' }],
    'every',
    {
      enabled: isServerlessEnabled && enabled,
    },
  ).hasPermissions

  const securityQuery = useListSecurityProjectsQuery({
    enabled: hasListSecurityPermission,
  })

  return {
    resourceType: 'security',
    data: securityQuery.data?.items,
    isLoading: securityQuery.isLoading,
    error: securityQuery.error,
    isEnabled: hasListSecurityPermission,
  }
}
